/**
 * @akson/cortex-utilities - Bulk Operations Manager
 * Intelligent batch processing with concurrency control and error recovery
 */

import { EventEmitter } from "node:events";

export interface BulkOptions {
  /** Maximum items to process in a single batch */
  batchSize?: number;
  /** Maximum concurrent batches to process */
  concurrency?: number;
  /** Whether to retry failed items */
  retryFailedItems?: boolean;
  /** Maximum number of retries per item */
  maxRetries?: number;
  /** Delay between retries in milliseconds */
  retryDelay?: number;
  /** Exponential backoff factor for retries */
  retryBackoffFactor?: number;
  /** Continue processing on error or stop */
  onError?: "continue" | "stop";
  /** Progress callback */
  onProgress?: (progress: BulkProgress) => void;
  /** Item completion callback */
  onItemComplete?: (item: any, result: any, index: number) => void;
  /** Item error callback */
  onItemError?: (item: any, error: Error, index: number) => void;
  /** Batch completion callback */
  onBatchComplete?: (batchIndex: number, results: any[]) => void;
  /** Whether to validate items before processing */
  validateBeforeProcess?: boolean;
  /** Custom validation function */
  validator?: (item: any) => boolean | Promise<boolean>;
  /** Whether to collect detailed metrics */
  collectMetrics?: boolean;
}

export interface BulkProgress {
  /** Total number of items to process */
  total: number;
  /** Number of successfully processed items */
  processed: number;
  /** Number of failed items */
  failed: number;
  /** Number of skipped items (validation failed) */
  skipped: number;
  /** Current processing percentage */
  percentage: number;
  /** Estimated time remaining in milliseconds */
  estimatedTimeRemaining?: number;
  /** Current batch being processed */
  currentBatch?: number;
  /** Total number of batches */
  totalBatches?: number;
  /** Average processing time per item */
  averageTimePerItem?: number;
}

export interface BulkResult<T> {
  /** Successfully processed items and their results */
  successful: Array<{ item: any; result: T; index: number }>;
  /** Failed items and their errors */
  failed: Array<{ item: any; error: Error; index: number; retries: number }>;
  /** Skipped items (validation failed) */
  skipped: Array<{ item: any; reason: string; index: number }>;
  /** Total number of items */
  total: number;
  /** Number of successful items */
  successCount: number;
  /** Number of failed items */
  failureCount: number;
  /** Number of skipped items */
  skippedCount: number;
  /** Total processing duration in milliseconds */
  duration: number;
  /** Detailed metrics if enabled */
  metrics?: BulkMetrics;
}

export interface BulkMetrics {
  /** Start time */
  startTime: Date;
  /** End time */
  endTime: Date;
  /** Total duration in milliseconds */
  totalDuration: number;
  /** Average time per item */
  averageTimePerItem: number;
  /** Fastest item processing time */
  fastestItem: number;
  /** Slowest item processing time */
  slowestItem: number;
  /** Processing times for each batch */
  batchTimes: number[];
  /** Retry statistics */
  retryStats: {
    totalRetries: number;
    successfulRetries: number;
    failedRetries: number;
  };
  /** Memory usage statistics */
  memoryStats?: {
    initial: number;
    peak: number;
    final: number;
  };
}

export class BulkOperationManager<T = any> extends EventEmitter {
  private readonly defaultOptions: Required<BulkOptions> = {
    batchSize: 10,
    concurrency: 3,
    retryFailedItems: true,
    maxRetries: 3,
    retryDelay: 1000,
    retryBackoffFactor: 2,
    onError: "continue",
    onProgress: () => {},
    onItemComplete: () => {},
    onItemError: () => {},
    onBatchComplete: () => {},
    validateBeforeProcess: false,
    validator: () => true,
    collectMetrics: false,
  };

  constructor(private options: BulkOptions = {}) {
    super();
    this.options = { ...this.defaultOptions, ...options };
  }

  /**
   * Execute a bulk operation on multiple items
   */
  async execute<R>(
    items: T[],
    operation: (item: T, index: number) => Promise<R>,
    customOptions?: BulkOptions
  ): Promise<BulkResult<R>> {
    const options = { ...this.defaultOptions, ...this.options, ...customOptions } as Required<BulkOptions>;
    const startTime = Date.now();
    const initialMemory = process.memoryUsage().heapUsed;

    const result: BulkResult<R> = {
      successful: [],
      failed: [],
      skipped: [],
      total: items.length,
      successCount: 0,
      failureCount: 0,
      skippedCount: 0,
      duration: 0,
    };

    if (options.collectMetrics) {
      result.metrics = {
        startTime: new Date(),
        endTime: new Date(),
        totalDuration: 0,
        averageTimePerItem: 0,
        fastestItem: Number.POSITIVE_INFINITY,
        slowestItem: 0,
        batchTimes: [],
        retryStats: {
          totalRetries: 0,
          successfulRetries: 0,
          failedRetries: 0,
        },
        memoryStats: {
          initial: initialMemory,
          peak: initialMemory,
          final: initialMemory,
        },
      };
    }

    // Validate items if validation is enabled
    if (options.validateBeforeProcess) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const isValid = await this.validateItem(item, options.validator!);
        if (!isValid) {
          result.skipped.push({ item, reason: "Validation failed", index: i });
          result.skippedCount++;
          this.updateProgress(result, options);
        }
      }
    }

    // Filter out skipped items
    const itemsToProcess = items.filter(
      (_item, index) => !result.skipped.some((s) => s.index === index)
    );

    // Create batches
    const batches = this.createBatches(itemsToProcess, options.batchSize!);
    const totalBatches = batches.length;

    // Process batches with concurrency control
    const concurrentBatches = [];
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const batchPromise = this.processBatch(
        batch,
        operation,
        result,
        options,
        batchIndex,
        totalBatches
      );

      concurrentBatches.push(batchPromise);

      // Control concurrency
      if (
        concurrentBatches.length >= options.concurrency! ||
        batchIndex === batches.length - 1
      ) {
        const _batchResults = await Promise.allSettled(concurrentBatches);

        // Check if we should stop on error
        if (options.onError === "stop" && result.failureCount > 0) {
          break;
        }

        concurrentBatches.length = 0;
      }
    }

    // Calculate final metrics
    result.duration = Date.now() - startTime;
    result.successCount = result.successful.length;
    result.failureCount = result.failed.length;

    if (result.metrics) {
      result.metrics.endTime = new Date();
      result.metrics.totalDuration = result.duration;
      result.metrics.averageTimePerItem = result.duration / result.total;
      result.metrics.memoryStats!.final = process.memoryUsage().heapUsed;
    }

    this.emit("complete", result);
    return result;
  }

  /**
   * Process a single batch of items
   */
  private async processBatch<R>(
    batch: Array<{ item: T; originalIndex: number }>,
    operation: (item: T, index: number) => Promise<R>,
    result: BulkResult<R>,
    options: Required<BulkOptions>,
    batchIndex: number,
    totalBatches: number
  ): Promise<void> {
    const batchStartTime = Date.now();
    const batchResults: any[] = [];

    for (const { item, originalIndex } of batch) {
      const itemStartTime = Date.now();

      try {
        const itemResult = await this.executeWithRetry(
          () => operation(item, originalIndex),
          options,
          result.metrics
        );

        result.successful.push({
          item,
          result: itemResult,
          index: originalIndex,
        });
        batchResults.push(itemResult);

        const itemDuration = Date.now() - itemStartTime;
        this.updateMetrics(result.metrics, itemDuration);

        options.onItemComplete(item, itemResult, originalIndex);
        this.emit("itemComplete", {
          item,
          result: itemResult,
          index: originalIndex,
        });
      } catch (error) {
        result.failed.push({
          item,
          error: error as Error,
          index: originalIndex,
          retries: options.maxRetries,
        });

        options.onItemError(item, error as Error, originalIndex);
        this.emit("itemError", { item, error, index: originalIndex });

        if (options.onError === "stop") {
          throw error;
        }
      }

      this.updateProgress(result, options, batchIndex, totalBatches);
    }

    const batchDuration = Date.now() - batchStartTime;
    if (result.metrics) {
      result.metrics.batchTimes.push(batchDuration);
    }

    options.onBatchComplete(batchIndex, batchResults);
    this.emit("batchComplete", { batchIndex, results: batchResults });
  }

  /**
   * Execute an operation with retry logic
   */
  private async executeWithRetry<R>(
    operation: () => Promise<R>,
    options: Required<BulkOptions>,
    metrics?: BulkMetrics
  ): Promise<R> {
    let lastError: Error | undefined;
    let delay = options.retryDelay;

    for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
      try {
        const result = await operation();

        if (metrics && attempt > 0) {
          metrics.retryStats.successfulRetries++;
        }

        return result;
      } catch (error) {
        lastError = error as Error;

        if (metrics) {
          metrics.retryStats.totalRetries++;
        }

        if (attempt === options.maxRetries) {
          if (metrics) {
            metrics.retryStats.failedRetries++;
          }
          break;
        }

        if (!options.retryFailedItems) {
          break;
        }

        // Exponential backoff
        await this.sleep(delay);
        delay *= options.retryBackoffFactor;
      }
    }

    throw lastError;
  }

  /**
   * Create batches from items
   */
  private createBatches<I>(
    items: I[],
    batchSize: number
  ): Array<Array<{ item: I; originalIndex: number }>> {
    const batches: Array<Array<{ item: I; originalIndex: number }>> = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize).map((item, offset) => ({
        item,
        originalIndex: i + offset,
      }));
      batches.push(batch);
    }

    return batches;
  }

  /**
   * Validate a single item
   */
  private async validateItem(
    item: T,
    validator: (item: any) => boolean | Promise<boolean>
  ): Promise<boolean> {
    try {
      return await validator(item);
    } catch (_error) {
      return false;
    }
  }

  /**
   * Update progress information
   */
  private updateProgress(
    result: BulkResult<any>,
    options: Required<BulkOptions>,
    currentBatch?: number,
    totalBatches?: number
  ): void {
    const processed = result.successful.length;
    const failed = result.failed.length;
    const skipped = result.skipped.length;
    const completed = processed + failed + skipped;
    const percentage = Math.round((completed / result.total) * 100);

    const progress: BulkProgress = {
      total: result.total,
      processed,
      failed,
      skipped,
      percentage,
      currentBatch,
      totalBatches,
    };

    // Estimate time remaining
    if (result.metrics && completed > 0) {
      const elapsedTime = Date.now() - result.metrics.startTime.getTime();
      const averageTimePerItem = elapsedTime / completed;
      const remainingItems = result.total - completed;
      progress.estimatedTimeRemaining = Math.round(
        averageTimePerItem * remainingItems
      );
      progress.averageTimePerItem = averageTimePerItem;
    }

    options.onProgress(progress);
    this.emit("progress", progress);
  }

  /**
   * Update metrics with item processing time
   */
  private updateMetrics(
    metrics: BulkMetrics | undefined,
    itemDuration: number
  ): void {
    if (!metrics) return;

    metrics.fastestItem = Math.min(metrics.fastestItem, itemDuration);
    metrics.slowestItem = Math.max(metrics.slowestItem, itemDuration);

    const currentMemory = process.memoryUsage().heapUsed;
    metrics.memoryStats!.peak = Math.max(
      metrics.memoryStats!.peak,
      currentMemory
    );
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Cancel ongoing bulk operation
   */
  cancel(): void {
    this.emit("cancelled");
  }
}

// Export convenience methods
export function createBulkManager<T = any>(
  options?: BulkOptions
): BulkOperationManager<T> {
  return new BulkOperationManager<T>(options);
}
